import { createContext, useContext } from "react";
import type { BillingLabel } from "@/domain/billing/state";

export interface AuthUser {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly isStaff: boolean;
}

export interface AuthOrg {
  readonly id: string;
  readonly name: string;
  readonly plan: "trial" | "standard";
  readonly subscriptionStatus:
    | "trialing"
    | "active"
    | "past_due"
    | "cancelled"
    | "expired";
  readonly trialEndsAt: string;
  readonly suspendedAt: string | null;
  readonly suspensionReason: string | null;
}

export interface AuthMembership {
  readonly id: string;
  readonly isOwner: boolean;
  readonly isCleaner: boolean;
  readonly onboardingCompleted: boolean;
  readonly isAccountCreator: boolean;
}

export interface AuthCapabilities {
  readonly maxTeamSize: number | null;
}

export interface AuthBillingState {
  readonly label: BillingLabel;
  readonly trialDaysRemaining: number | null;
  readonly showsBanner: boolean;
  readonly checkInBlocked: boolean;
  readonly editingBlocked: boolean;
}

export interface AuthData {
  readonly user: AuthUser;
  readonly org: AuthOrg;
  readonly membership: AuthMembership;
  readonly capabilities: AuthCapabilities;
  readonly billingState: AuthBillingState;
}

export const AuthContext = createContext<AuthData | null>(null);

export function useAuth(): AuthData {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthGuard");
  }
  return context;
}
